// ─────────────────────────────────────────────────────────────────────
// ONE VALUE-SEMANTIC FINGERPRINT OF A MATCH'S RESULT (#559 review).
//
// Two things need to compare results across a refresh, and neither may use
// object identity:
//
//   the REFRESHER  decides whether an incoming document changed anything;
//   a SCORE ROW    decides whether the stored result moved under its draft.
//
// Every refresh is freshly parsed JSON, so every object in it is new. A
// dependency on `match.score` — a KM/seriespill result is an OBJECT — therefore
// fires on every adoption, including one for a different court, and clears a
// draft nobody asked to discard. That is the silent loss the whole design exists
// to prevent, reintroduced by a reference comparison.
//
// AND IT HAS TO BE DEEP. A KM score is `{ t1, t2, sets }` where `t1`/`t2` are
// SET COUNTS: 6-4 6-4 and 6-3 6-2 are both «2–0», so a correction can change the
// sets while the counts stay put. A fingerprint that stopped at t1/t2 would call
// that «no change» and leave the portal stale.
//
// KEYS ARE SORTED, so two orderings of the same content agree. `JSON.stringify`
// alone does not guarantee that: the local object may be engine-built and the
// incoming one parsed from a document serialised elsewhere, and a fingerprint
// that differed for identical values would clear a draft — the exact direction
// this must never be wrong in.
//
// ONE DEFINITION, BOTH CALLERS. Two fingerprints that agree today are two that
// can disagree later, and the failure would be invisible in both.
// ─────────────────────────────────────────────────────────────────────

/** Stable, order-independent serialisation of any JSON-ish value. */
function stable(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${k}:${stable(v)}`).join(',')}}`;
  }
  return String(value);
}

/**
 * Everything that makes a result what it is — and nothing else.
 *
 * Deliberately NOT the whole match: a pairing change, a court reassignment or a
 * new time are not results, and a row's draft should survive them. What counts
 * is the flat score (padel24 and the americano family), the structured score
 * (KM and seriespill) and whether the match is finished.
 */
export function resultFingerprint(match: unknown): string {
  const m = (match ?? {}) as Record<string, unknown>;
  return stable({
    score1: m.score1 ?? null,
    score2: m.score2 ?? null,
    score: m.score ?? null,
    completed: m.completed ?? null,
  });
}
